import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { PT_BR_TOOLS, PT_BR_CATEGORIES } from "@/lib/pt-br-tools-catalog";
import { PT_BR_PDF_TOOLS } from "@/lib/pt-br-pdf-catalog";
import { Sparkles, FileText, ArrowRight, ShieldCheck, Zap, Disc3 } from "lucide-react";

export const Route = createFileRoute("/pt-br/")({
  head: () => ({
    meta: [
      { title: "Ferramentas Gratuitas para Sala de Aula, Sorteios e PDF — Picamini.online" },
      {
        name: "description",
        content:
          "Sorteadores de nomes, roletas interativas da sorte, geradores de equipes e mais de 26 ferramentas PDF gratuitas. 100% privado, sem cadastro e direto no seu navegador.",
      },
      {
        name: "keywords",
        content:
          "sorteador de nomes, roleta da sorte online, roleta de nomes, juntar pdf gratis, gerador de equipes, ferramentas para professores, picamini brasil",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Ferramentas Gratuitas para Sala de Aula e PDF — Picamini.online" },
      {
        property: "og:description",
        content: "Sorteadores rápidos, roletas interativas e 26+ ferramentas PDF 100% privadas no seu navegador.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/pt-br/" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/pt-br/" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/" },
    ],
  }),
  component: PtBrHomePage,
});

function PtBrHomePage() {
  const featuredTools = PT_BR_TOOLS.filter((t) => t.featured);

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader lang="pt-BR" />

      <main className="flex-1">
        {/* HERO SECTION */}
        <section className="bg-gradient-to-b from-primary/5 via-background to-background py-16 sm:py-20 border-b border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 text-center space-y-5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary">
              <Sparkles className="size-4" /> 37 Ferramentas Interativas + 26 Utilitários PDF Grátis
            </span>

            <h1 className="text-3xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl text-foreground max-w-4xl mx-auto leading-tight">
              Sorteadores, Roletas e Ferramentas PDF Gratuitas Online
            </h1>

            <p className="max-w-3xl mx-auto text-base sm:text-lg text-muted-foreground leading-relaxed">
              Ferramentas intuitivas e ultrarrápidas para professores, estudantes, organizadores de eventos e profissionais. Faça sorteios, girar roletas e editar arquivos PDF com 100% de privacidade no seu navegador.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <Link
                to="/pt-br/pdf-tools"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-extrabold text-primary-foreground shadow-md transition-all hover:bg-primary/90 hover:shadow-lg"
              >
                <FileText className="size-4" /> Ver Ferramentas PDF
              </Link>
              <Link
                to="/pt-br/$slug"
                params={{ slug: "roleta-da-sorte-online" }}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3.5 text-sm font-extrabold text-foreground transition-all hover:bg-secondary"
              >
                <Disc3 className="size-4 text-primary" /> Girar Roleta da Sorte
              </Link>
            </div>
          </div>
        </section>

        {/* FEATURED TOOLS GRID */}
        <section className="py-12 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Zap className="size-6 text-primary" /> Ferramentas em Destaque
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featuredTools.map((tool) => (
              <Link
                key={tool.slug}
                to="/pt-br/$slug"
                params={{ slug: tool.slug }}
                className="surface-card p-6 rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-md flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                      <Sparkles className="size-5" />
                    </span>
                    <h3 className="font-bold text-base text-foreground">{tool.name}</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{tool.tagline}</p>
                </div>
                <div className="pt-4 flex items-center gap-1.5 text-xs font-bold text-primary">
                  Acessar ferramenta <ArrowRight className="size-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ALL CATEGORIES SECTION */}
        <section className="py-12 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-12">
          {PT_BR_CATEGORIES.map((cat) => {
            const catTools = PT_BR_TOOLS.filter((t) => t.category === cat);
            return (
              <div key={cat} className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground border-b border-border/60 pb-3 flex items-center gap-2">
                  <span className="size-3 rounded-full bg-primary" /> {cat}
                </h2>

                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:grid-cols-4">
                  {catTools.map((tool) => (
                    <Link
                      key={tool.slug}
                      to="/pt-br/$slug"
                      params={{ slug: tool.slug }}
                      className="surface-card p-5 rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-md flex flex-col justify-between"
                    >
                      <div className="space-y-2.5">
                        <h3 className="font-bold text-sm text-foreground">{tool.name}</h3>
                        <p className="text-xs text-muted-foreground line-clamp-2">{tool.tagline}</p>
                      </div>
                      <div className="pt-3 flex items-center gap-1 text-xs font-bold text-primary">
                        Abrir <ArrowRight className="size-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        {/* PDF TOOLS PROMO HUB */}
        <section className="bg-primary/5 py-16 border-y border-border/60">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-8 text-center">
            <div className="space-y-3 max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-3.5 py-1 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                <ShieldCheck className="size-4" /> 100% Processamento Local e Privado
              </span>
              <h2 className="text-3xl font-extrabold text-foreground">Suíte PDF Toolkit Pro em Português</h2>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                26+ ferramentas PDF para juntar, dividir, compactar, editar, converter para Word e Excel sem enviar seus arquivos confidenciais para nenhum servidor.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto">
              {PT_BR_PDF_TOOLS.slice(0, 8).map((pdfTool) => (
                <Link
                  key={pdfTool.slug}
                  to="/pt-br/pdf-tools/$slug"
                  params={{ slug: pdfTool.slug }}
                  className="p-4 rounded-xl border bg-card text-left transition-all hover:-translate-y-0.5 hover:shadow-sm space-y-1.5"
                >
                  <p className="font-bold text-sm text-foreground">{pdfTool.name}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{pdfTool.description}</p>
                </Link>
              ))}
            </div>

            <div>
              <Link
                to="/pt-br/pdf-tools"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-md transition-all hover:bg-primary/90"
              >
                Ver Todas as 26 Ferramentas PDF <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter lang="pt-BR" />
    </div>
  );
}
